import React, { useState } from "react";
import { ATTESTATION_STATEMENT, STAKEHOLDER_TYPES } from "./PolicyLifecyclePanel";

// Acknowledgments (prototype tab 3): per-policy attestation progress for the version in force —
// named assignments (Pending / Overdue / Attested), the completion vs org members, and the
// signed-in user's own "Read & attest" (assigned or self-service).
const PolicyAttestationsTab = ({ summary, apiCall, onChanged, onOpenPolicy }) => {
    const [type, setType] = useState("Employee");
    const [busyId, setBusyId] = useState(null);
    const [expandedId, setExpandedId] = useState(null);
    const [dialog, setDialog] = useState(null); // { policy, assignmentId? }
    const [checked, setChecked] = useState(false);
    const [error, setError] = useState(null);

    const attest = async () => {
        const p = dialog.policy;
        setBusyId(p.id); setError(null);
        try {
            if (dialog.assignmentId) await apiCall("POST", `/security-insights/policy-docs/${p.id}/attestations/${dialog.assignmentId}/attest`);
            else await apiCall("POST", `/security-insights/policy-docs/${p.id}/attest`, { stakeholderType: type });
            setDialog(null); setChecked(false);
            onChanged?.();
        } catch (e) { setError(e?.response?.data?.message || "Could not record the attestation."); }
        finally { setBusyId(null); }
    };

    if (!summary) return <p className="text-xs text-gray-400">Loading acknowledgments…</p>;
    if (summary.length === 0)
        return <p className="text-xs text-gray-400">No policy is in force yet — acknowledgments begin once a policy is published and effective.</p>;

    return (
        <div className="space-y-3">
            <div className="text-xs text-gray-500 bg-white border border-gray-200 rounded-lg px-3 py-2">
                Attestations bind a named employee or stakeholder to one published version. Open a policy to assign recipients; recipients attest here or from My Tasks.
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-500">
                Self-service attest as:
                <select value={type} onChange={e => setType(e.target.value)} className="border border-gray-200 rounded-lg px-2 py-1">
                    {STAKEHOLDER_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
            </div>
            {error && <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}
            {summary.map(p => {
                const pct = p.memberCount > 0 ? Math.min(100, Math.round(100 * p.attestedCount / p.memberCount)) : 0;
                const assignedPct = p.assignedCount > 0 ? Math.round(100 * p.attestedAssignedCount / p.assignedCount) : null;
                const isOpen = expandedId === p.id;
                return (
                    <div key={p.id} className="bg-white border border-gray-200 rounded-xl p-3">
                        <div className="flex items-center gap-3 flex-wrap">
                            <div className="min-w-0">
                                <div className="text-sm font-medium text-gray-800 truncate">{p.title} <span className="text-gray-400 text-xs">v{p.version}</span></div>
                                <div className="text-xs text-gray-500">
                                    {assignedPct !== null ? `${p.attestedAssignedCount} of ${p.assignedCount} assigned attested` : "No assignments"}
                                    {p.overdueCount > 0 && <span className="text-red-600 font-medium"> · {p.overdueCount} overdue</span>}
                                    {" · "}{p.attestedCount} total · {pct}% of org members
                                </div>
                            </div>
                            <div className="flex-1 min-w-[120px] h-2 bg-gray-100 rounded-full overflow-hidden">
                                <div className="h-full bg-green-500 rounded-full" style={{ width: `${assignedPct ?? pct}%` }} />
                            </div>
                            {p.currentUserAttested
                                ? <span className="text-xs text-green-700 bg-green-50 border border-green-200 rounded-lg px-2.5 py-1 font-medium">✓ Attested</span>
                                : <button disabled={busyId === p.id} onClick={() => { setDialog({ policy: p, assignmentId: p.currentUserPendingAssignmentId }); setChecked(false); }}
                                    className="text-xs px-3 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium disabled:opacity-50">
                                    Read &amp; attest{p.currentUserPendingAssignmentId ? " (assigned)" : ""}
                                </button>}
                            {onOpenPolicy && <button onClick={() => onOpenPolicy(p.id, "attestations")} className="text-xs text-indigo-700 underline">Open</button>}
                            <button onClick={() => setExpandedId(isOpen ? null : p.id)} className="text-xs text-gray-400 hover:text-gray-600">{isOpen ? "▾" : "▸"}</button>
                        </div>
                        {isOpen && (
                            <div className="mt-2 pt-2 border-t border-gray-100 space-y-0.5 text-xs">
                                {(p.assignments || []).map(a => (
                                    <div key={a.id} className="flex items-center gap-2 text-gray-600">
                                        <span className="font-medium">{a.name || `user ${a.userId}`}</span>
                                        <span className="text-gray-400">{a.stakeholderType}</span>
                                        <span className="text-gray-400">due {new Date(a.dueDate).toLocaleDateString()}</span>
                                        <span className={`px-1.5 py-0.5 rounded-full text-[10px] border ${a.status === "Attested" ? "bg-green-50 text-green-700 border-green-200" : a.status === "Overdue" ? "bg-red-50 text-red-700 border-red-200" : "bg-blue-50 text-blue-700 border-blue-200"}`}>{a.status}</span>
                                    </div>
                                ))}
                                {(p.attesters || []).filter(r => r.source !== "Assigned").map((r, i) => (
                                    <div key={`s-${i}`} className="flex items-center gap-2 text-gray-600">
                                        <span className="font-medium">{r.name || "Unknown user"}</span>
                                        <span className="text-gray-400">{r.stakeholderType} · self-service</span>
                                        <span className="text-gray-400 ml-auto">{new Date(r.attestedAt).toLocaleString()}</span>
                                    </div>
                                ))}
                                {(p.assignments || []).length === 0 && (p.attesters || []).length === 0 && <p className="text-gray-400">No acknowledgments for this version yet.</p>}
                            </div>
                        )}
                    </div>
                );
            })}
            {dialog && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-5 space-y-3">
                        <div className="text-sm font-bold text-gray-800">{dialog.policy.title} · v{dialog.policy.version}</div>
                        <div className="text-xs text-gray-600">Read the policy document before attesting (open the policy in the register to download it).</div>
                        <label className="flex items-start gap-2 text-xs text-gray-700"><input type="checkbox" checked={checked} onChange={e => setChecked(e.target.checked)} className="mt-0.5" /> {ATTESTATION_STATEMENT}</label>
                        <div className="flex gap-2">
                            <button onClick={() => setDialog(null)} className="flex-1 text-sm px-4 py-2 border border-gray-300 rounded-lg">Cancel</button>
                            <button onClick={attest} disabled={!checked || busyId !== null} className="flex-1 text-sm px-4 py-2 bg-indigo-600 text-white rounded-lg disabled:opacity-40">Record attestation</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PolicyAttestationsTab;
